import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Camera, Upload, X, Brain, Mic, MicOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';

import {
    createObservation,
    getAreasWithId,
    getUserCompanyById, // Import getUserCompanyById
    // getImageDescriptionFromUrl 
} from '../../services/observationAPI';
import { uploadEvidenceToServer } from '../../services/operations/profileAPI';

// Hook to get authentication status using Redux
const useAuthStatus = () => {
    const { token } = useSelector((state) => state.auth);
    const isLoggedIn = !!token;
    return { isLoggedIn };
};


const SubmitObservation = () => {
    const navigate = useNavigate();
    const { adminId } = useParams();
    const { isLoggedIn } = useAuthStatus();

    const [areas, setAreas] = useState([]);
    const [adminCompany, setAdminCompany] = useState(null); // State to store admin company details
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // State for photo handling
    const [photo, setPhoto] = useState(null); // The actual File object
    const [uploadedPhotoName, setUploadedPhotoName] = useState(''); // Name of the uploaded file for display
    const [cloudinaryPhotoUrl, setCloudinaryPhotoUrl] = useState(''); // URL from Cloudinary
    const [isUploadingPhoto, setIsUploadingPhoto] = useState(false); // Loading state for photo upload

    // State for form fields
    const [area, setArea] = useState('');
    const [type, setType] = useState('unsafe act'); // Default to 'unsafe act'
    const [description, setDescription] = useState('');
    const [notes, setNotes] = useState('');
    const [category, setCategory] = useState('');
    const _id = useSelector((state) => state?.profile?.user?._id)

    // State for AI description
    const [isGettingAIDescription, setIsGettingAIDescription] = useState(false);

    // State for speech-to-text
    const [isListening, setIsListening] = useState(false);
    const recognitionRef = useRef(null); // Ref for SpeechRecognition object

    const unsafeActCategories = [
        'Behavior', 'Attitude', 'Negligence', 'Over Confidence',
        'Underestimation', 'Short cuts', 'Overlooking consequences', 'Procrastination'
    ];

    const unsafeConditionCategories = [
        'Lack of Knowledge', 'External Factors', 'Incompetency', 'Machine/Tool Condition',
        'Experience', 'Unsafe action by others', 'Natural calamity'
    ];


    useEffect(() => {
        const fetchData = async () => {
            if (!adminId) {
                console.error("adminId is missing from the URL");
                toast.error("Admin ID is missing. Cannot load data.");
                setLoading(false);
                return;
            }

            try {
                // Fetch areas and admin company details concurrently
                const [areasRes, companyRes] = await Promise.all([
                    getAreasWithId(adminId),
                    getUserCompanyById(adminId) // Fetch admin company details
                ]);

                setAreas(areasRes);
                setAdminCompany(companyRes.company); // Store admin company details

            } catch (error) {
                console.error('Error fetching data:', error);
                // Provide more specific error messages based on the type of fetch failure
                if (error.message.includes('areas')) {
                    toast.error('Failed to load areas.');
                } else if (error.message.includes('company')) { // Updated error message check
                    toast.error('Failed to load admin company details.');
                } else {
                    toast.error('Failed to load required data');
                }

            } finally {
                setLoading(false);
            }
        };

        fetchData();

    }, [adminId]); // Re-run effect if adminId changes

    // Initialize SpeechRecognition API
    useEffect(() => {
        // Check for browser compatibility
        if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.continuous = false; // Stop after a single utterance
            recognitionRef.current.interimResults = false; // Only return final results
            recognitionRef.current.lang = 'en-US';

            // Event handler for when speech is recognized
            recognitionRef.current.onresult = (event) => {
                const transcript = Array.from(event.results)
                    .map((result) => result[0].transcript)
                    .join('');
                setDescription((prevDesc) => prevDesc + (prevDesc ? ' ' : '') + transcript); // Append to existing description
            };

            // Event handler for when speech recognition ends
            recognitionRef.current.onend = () => {
                setIsListening(false);
            };

            // Event handler for errors
            recognitionRef.current.onerror = (event) => {
                console.error('Speech recognition error:', event.error);
                toast.error(`Speech recognition error: ${event.error}`);
                setIsListening(false);
            };
        } else {
            console.warn('Speech Recognition API not supported in this browser.');
            toast.warn('Speech-to-text is not supported in your browser.');
        }

        // Cleanup on component unmount
        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
        };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!category) {
            toast.error('Please select a category');
            return;
        }

        if (!area || !description) {
            toast.error('Please fill in all required fields');
            return;
        }

        setSubmitting(true);

        try {
            // Construct the observation data to be sent
            const observationData = {
                area,
                type,
                description,
                photos: cloudinaryPhotoUrl ? [cloudinaryPhotoUrl] : [], // Use the Cloudinary URL
                notes,
                category,
                adminId,
                observedBy: _id,
            };

            await createObservation(observationData);

            if (isLoggedIn) {
                toast.success('Observation submitted successfully');
                navigate('/');
            } else {
                toast.success('Observation submitted successfully. Thank you!');
                // Reset form for anonymous users
                setArea('');
                setType('unsafe act');
                setDescription('');
                setNotes('');
                setCategory('');
                setPhoto(null);
                setUploadedPhotoName(''); // Reset uploaded photo name
                setCloudinaryPhotoUrl(''); // Reset Cloudinary URL
            }

        } catch (error) {
            console.error('Error submitting observation:', error);
            if (error.response && error.response.status === 401) {
                toast.error('Submission failed: Authentication required for this action.');
            } else {
                toast.error('Failed to submit observation');
            }
        } finally {
            setSubmitting(false);
        }
    };

    // Handle photo selection and immediate upload to Cloudinary
    const handlePhotoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setPhoto(file);
            setUploadedPhotoName(file.name);
            setIsUploadingPhoto(true);
            setCloudinaryPhotoUrl(''); // Clear previous URL

            try {
                const uploadResult = await uploadEvidenceToServer(file);
                if (uploadResult) {
                    setCloudinaryPhotoUrl(uploadResult);
                    toast.success('Photo uploaded successfully!');
                } else {
                    toast.error('Failed to upload photo to Cloudinary.');
                    setPhoto(null);
                    setUploadedPhotoName('');
                }
            } catch (error) {
                console.error('Error uploading photo:', error);
                toast.error('Error uploading photo. Please try again.');
                setPhoto(null);
                setUploadedPhotoName('');
            } finally {
                setIsUploadingPhoto(false);
            }
        }
    };

    const removePhoto = () => {
        setPhoto(null);
        setUploadedPhotoName('');
        setCloudinaryPhotoUrl('');
    };

    // Handle getting AI description for the uploaded image
    // const handleGetAIDescription = async () => {
    //     if (!cloudinaryPhotoUrl) {
    //         toast.error('Please upload a photo first to get AI description.');
    //         return;
    //     }

    //     setIsGettingAIDescription(true);
    //     try {
    //         const descriptionResult = await getImageDescriptionFromUrl(cloudinaryPhotoUrl);
    //         if (descriptionResult && descriptionResult.description) {
    //             setDescription(descriptionResult.description);
    //             toast.success('AI description generated!');
    //         } else {
    //             toast.error('Failed to get AI description. Please try again.');
    //         }
    //     } catch (error) {
    //         console.error('Error getting AI description:', error);
    //         toast.error('Error getting AI description. Please try again.');
    //     } finally {
    //         setIsGettingAIDescription(false);
    //     }
    // };

    // Handle speech-to-text functionality
    const toggleSpeechRecognition = () => {
        if (recognitionRef.current) {
            if (isListening) {
                recognitionRef.current.stop();
                setIsListening(false);
            } else {
                // No change here, as the previous version already appended to existing text.
                recognitionRef.current.start();
                setIsListening(true);
                toast.success('Listening for speech...');
            }
        } else {
            toast.warn('Speech recognition is not available.');
        }
    };


    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gray-50">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
            </div>
        );
    }

    const getCategories = () => {
        if (type === 'unsafe act') {
            return unsafeActCategories;
        } else if (type === 'unsafe condition') {
            return unsafeConditionCategories;
        } else {
            return [];
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 sm:p-6 lg:p-8">
            <div className="max-w-4xl w-full">
                {/* Header Section */}
                <div className="mb-8 text-center">
                    <h1 className="text-4xl font-extrabold text-gray-900 mb-2">Submit a New Observation</h1>
                    <p className="text-lg text-gray-600">Help us maintain a safer environment by reporting observations.</p>
                    {!isLoggedIn && (
                        <p className="text-sm text-gray-500 mt-3 p-2 bg-yellow-50 rounded-md inline-block">
                            You are submitting this observation <span className="font-semibold text-yellow-800">anonymously</span>.
                        </p>
                    )}
                    {/* Display company name if adminCompany is loaded and has companyName */}
                    {adminCompany && (
                        <p className="text-sm text-gray-500 mt-1">
                            For Company: <span className="font-medium text-gray-700">{adminCompany}</span>
                        </p>
                    )}
                    {/* Fallback if adminCompany or company name is not available but adminId is present */}
                    {adminId && !adminCompany && !loading && (
                        <p className="text-sm text-gray-500 mt-1">
                            Company details not fully loaded for Admin ID: <span className="font-medium text-gray-700">{adminId}</span>
                        </p>
                    )}
                </div>

                {/* Form Section */}
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="bg-white rounded-xl shadow-xl p-6 sm:p-8">
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                            {/* Area Selection */}
                            <div>
                                <label htmlFor="area" className="block text-sm font-semibold text-gray-700 mb-1">
                                    Area <span className="text-red-500">*</span>
                                </label>
                                <select
                                    id="area"
                                    value={area}
                                    onChange={(e) => setArea(e.target.value)}
                                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition duration-150 ease-in-out"
                                    required
                                    disabled={!adminId || areas.length === 0}
                                >
                                    <option value="">Select an area</option>
                                    {areas.map((area) => (
                                        <option key={area._id} value={area._id}>
                                            {area.name}
                                        </option>
                                    ))}
                                </select>
                                {!adminId && (
                                    <p className="text-sm text-red-600 mt-2">Admin ID is missing from the URL. Cannot load areas.</p>
                                )}
                                {adminId && areas.length === 0 && !loading && (
                                    <p className="text-sm text-gray-500 mt-2">No areas found for this Admin ID.</p>
                                )}
                            </div>

                            {/* Observation Type Selection */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Observation Type <span className="text-red-500">*</span>
                                </label>
                                <div className="mt-2 flex flex-wrap gap-4">
                                    <label className="inline-flex items-center cursor-pointer">
                                        <input
                                            type="radio"
                                            value="unsafe act"
                                            checked={type === 'unsafe act'}
                                            onChange={(e) => { setType(e.target.value); setCategory(''); }}
                                            className="form-radio h-5 w-5 text-yellow-600 border-gray-300 focus:ring-yellow-500 transition duration-150 ease-in-out"
                                        />
                                        <span className="ml-2 text-gray-800 font-medium">Unsafe Act</span>
                                    </label>
                                    <label className="inline-flex items-center cursor-pointer">
                                        <input
                                            type="radio"
                                            value="unsafe condition"
                                            checked={type === 'unsafe condition'}
                                            onChange={(e) => { setType(e.target.value); setCategory(''); }}
                                            className="form-radio h-5 w-5 text-red-600 border-gray-300 focus:ring-red-500 transition duration-150 ease-in-out"
                                        />
                                        <span className="ml-2 text-gray-800 font-medium">Unsafe Condition</span>
                                    </label>
                                </div>
                            </div>

                            {/* Category Selection */}
                            <div className="col-span-full">
                                <label htmlFor="category" className="block text-sm font-semibold text-gray-700 mb-1">
                                    Category <span className="text-red-500">*</span>
                                </label>
                                <select
                                    id="category"
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value)}
                                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition duration-150 ease-in-out"
                                    required
                                >
                                    <option value="">Select a category</option>
                                    {getCategories().map((cat) => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                            </div>

                             {/* Photo Upload */}
                            <div className="col-span-full">
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Photo (Optional)
                                </label>
                                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg bg-gray-50 hover:bg-gray-100 transition duration-150 ease-in-out">
                                    <div className="space-y-2 text-center">
                                        {cloudinaryPhotoUrl ? (
                                            <img src={cloudinaryPhotoUrl} alt="Uploaded" className="mx-auto h-24 w-auto object-cover rounded-md shadow-sm" />
                                        ) : (
                                            <Camera className="mx-auto h-16 w-16 text-gray-400" />
                                        )}
                                        <div className="flex text-sm text-gray-600 justify-center">
                                            <label
                                                htmlFor="photo"
                                                className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                                            >
                                                <span>{isUploadingPhoto ? 'Uploading...' : 'Upload a file'}</span>
                                                <input
                                                    id="photo"
                                                    name="photo"
                                                    type="file"
                                                    className="sr-only"
                                                    accept="image/*"
                                                    onChange={handlePhotoUpload}
                                                    disabled={isUploadingPhoto}
                                                />
                                            </label>
                                            <p className="pl-1">or drag and drop</p>
                                        </div>
                                        <p className="text-xs text-gray-500">PNG, JPG up to 10MB</p>
                                    </div>
                                </div>

                                {uploadedPhotoName && (
                                    <div className="mt-4 bg-blue-50 p-3 rounded-lg flex items-center justify-between border border-blue-200">
                                        <div className="flex items-center">
                                            <Upload className="h-5 w-5 text-blue-500 mr-3" />
                                            <span className="text-sm font-medium text-blue-800 truncate">{uploadedPhotoName}</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={removePhoto}
                                            className="text-blue-400 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out rounded-full p-1"
                                            aria-label="Remove photo"
                                        >
                                            <X className="h-5 w-5" />
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Description with AI and Mic */}
                            <div className="col-span-full">
                                <label htmlFor="description" className="block text-sm font-semibold text-gray-700 mb-1">
                                    Description <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <textarea
                                        id="description"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        rows={4}
                                        className="block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm resize-y transition duration-150 ease-in-out pr-20" // Increased padding for two icons
                                        placeholder="Describe what you observed..."
                                        required
                                    ></textarea>
                                    <div className="absolute top-2 right-2 flex space-x-2">
                                        <button
                                            type="button"
                                            // onClick={handleGetAIDescription}
                                            disabled={!cloudinaryPhotoUrl || isGettingAIDescription || isUploadingPhoto}
                                            className={`p-2 rounded-full transition duration-200 ease-in-out ${
                                                !cloudinaryPhotoUrl || isGettingAIDescription || isUploadingPhoto
                                                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                    : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                                            }`}
                                            title="Get AI Description"
                                        >
                                            {isGettingAIDescription ? (
                                                <div className="animate-spin h-5 w-5 border-t-2 border-b-2 border-blue-600 rounded-full"></div>
                                            ) : (
                                                <Brain className="h-5 w-5" />
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={toggleSpeechRecognition}
                                            disabled={!recognitionRef.current}
                                            className={`p-2 rounded-full transition duration-200 ease-in-out ${
                                                !recognitionRef.current
                                                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                    : isListening
                                                    ? 'bg-red-100 text-red-600 hover:bg-red-200'
                                                    : 'bg-green-100 text-green-600 hover:bg-green-200'
                                            }`}
                                            title={isListening ? "Stop Listening" : "Start Speech-to-Text"}
                                        >
                                            {isListening ? <MicOff className="h-5 w-5 animate-pulse" /> : <Mic className="h-5 w-5" />}
                                        </button>
                                    </div>
                                </div>
                            </div>

                           

                            {/* Additional Notes */}
                            <div className="col-span-full">
                                <label htmlFor="notes" className="block text-sm font-semibold text-gray-700 mb-1">
                                    Additional Notes (Optional)
                                </label>
                                <textarea
                                    id="notes"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    rows={3}
                                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm resize-y transition duration-150 ease-in-out"
                                    placeholder="Any additional information..."
                                ></textarea>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-4 pt-4">
                        <button
                            type="button"
                            onClick={() => isLoggedIn ? navigate('/') : navigate('/')}
                            className="inline-flex justify-center py-2 px-6 border border-gray-300 shadow-sm text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-150 ease-in-out"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting || !adminId || areas.length === 0 || isUploadingPhoto || isGettingAIDescription || isListening}
                            className="inline-flex justify-center py-2 px-6 border border-transparent shadow-sm text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150 ease-in-out"
                        >
                            {submitting ? 'Submitting...' : 'Submit Observation'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SubmitObservation;
